import { recordCalibrationEvent } from '@framework/utils/operationCalibration';
import type { ModelElementId, ModelKey } from '@view-model/domain/key';
import type { CommandManager, ICommand } from '@model-framework/command';
import { CompositeCommand } from '@model-framework/command';
import { ModelElementPositionMap } from '@view-model/domain/model';
import { DragManager } from '@view-model/models/common/PositionSet';
import { PositionSetRepository } from '@view-model/models/common/PositionSet/infrastructure/PositionSetRepository';
import { ElementsMoveCommand } from '@view-model/command/basic-model/ElementsMoveCommand/ElementsMoveCommand';
import type { StickyModelContentsOperation } from '@view-model/adapter';
import { ModelLayout } from '@view-model/models/sticky/layout';
import { StickyModelElementPositionMapRepository } from '@view-model/infrastructure/basic-model/StickyModelElementPositionMapRepository';
import type { DragContext } from '@model-framework/ui';
import { RTDBPath } from 'balus-common';
import type { ModelId, StickyZoneId, ViewId, ViewModelId } from 'balus-common';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { getDefaultStore } from 'jotai';
import { dropTargetViewAtom } from './dropTargetViewAtom';
import { isFileContainerAssetBeingDeleted } from '@view-model/models/sticky/FileContainer/command/FileContainerAssetTrashCommand';

export class ElementDragManager {
  private startPositions: ModelElementPositionMap;
  private currentPositions: ModelElementPositionMap;
  private dragging: boolean;
  private calibrationDragStartedAt = 0;
  private readonly modelCommentDragManager: DragManager;
  private readonly descriptionPanelDragManager: DragManager;
  private readonly fileContainerDragManager: DragManager;
  private readonly positionMapRepository: StickyModelElementPositionMapRepository;
  private viewRectOperations?: Record<
    ViewId,
    {
      view: ViewEntity;
      rect?: Rect;
      operation?: StickyModelContentsOperation;
    }
  >;

  constructor(
    viewModelId: ViewModelId,
    modelId: ModelId,
    private readonly modelKey: ModelKey,
    private readonly viewId: ViewId,
    private readonly commandManager: CommandManager,
    private readonly stickyModelContentsOperation: StickyModelContentsOperation,
    private readonly getViewRectOperations: () => Record<
      ViewId,
      { view: ViewEntity; rect?: Rect; operation?: StickyModelContentsOperation }
    >,
    private readonly selectSingleView: (viewId?: ViewId) => void
  ) {
    this.startPositions = new ModelElementPositionMap();
    this.currentPositions = new ModelElementPositionMap();
    this.positionMapRepository = new StickyModelElementPositionMapRepository(viewModelId, modelId);
    this.modelCommentDragManager = new DragManager(
      new PositionSetRepository(RTDBPath.Comment.positionsPath(viewModelId, modelId))
    );
    this.descriptionPanelDragManager = new DragManager(
      new PositionSetRepository(RTDBPath.DescriptionPanel.positionsPath(viewModelId, modelId))
    );
    this.fileContainerDragManager = new DragManager(
      new PositionSetRepository(RTDBPath.FileContainer.positionsPath(viewModelId, modelId))
    );

    this.dragging = false;
  }

  /**
   * ドラッグ開始イベントハンドラ
   *
   * @param id ドラッグ対象のモデル要素ID（マウスポインタで掴んでいるモデル要素）
   * @param multiSelectionMode 複数選択モードかどうか
   */
  onDragStart(id: ModelElementId, isMultiSelected: boolean): void {
    this.stickyModelContentsOperation.selectForDragStart(id, isMultiSelected);
    const selectedIds = this.stickyModelContentsOperation.getSelectedIds();

    this.startPositions = this.stickyModelContentsOperation.selectedModelElementPositionMap();
    this.currentPositions = this.startPositions;

    // 位置の読み込みを待たないため、読み込みが終わる前の最初の onDrag は捨てられる。
    // 以降の onDrag は開始時点からの移動量で位置を計算し直すので、困るのはマウスを 1 回だけ動かして
    // ドロップした場合に限られ、受容している。
    this.descriptionPanelDragManager.dragStart(selectedIds).then();
    this.modelCommentDragManager.dragStart(selectedIds).then();
    this.fileContainerDragManager.dragStart(selectedIds).then();

    if (this.dragging) {
      console.warn(
        'NodeDragManager.onDragStart() is called multiple times before onDragEnd(). \
                It may cause an unpredictable problem.'
      );
    }
    this.calibrationDragStartedAt = performance.now();
    recordCalibrationEvent('drag.start', { selectedElements: selectedIds.length });
    this.dragging = true;
    this.viewRectOperations = this.getViewRectOperations();
  }

  async onDrag(context: DragContext): Promise<void> {
    recordCalibrationEvent('drag.move');
    this.currentPositions = this.startPositions.movePosition(
      context.x - context.dragStartX,
      context.y - context.dragStartY
    );

    const targetViewData = this.findDropTargetView(context);
    const currentViewRect = this.viewRectOperations?.[this.viewId].rect ?? undefined;
    const canDropIntoZone = this.stickyModelContentsOperation.getDroppableSelectedIdsIntoZone().length > 0;
    const stickyZoneIds =
      targetViewData && currentViewRect && canDropIntoZone
        ? this.findDropTargetZones(currentViewRect, targetViewData)
        : [];
    const dropTarget = targetViewData ? { viewId: targetViewData.view.id, stickyZoneIds } : null;
    getDefaultStore().set(dropTargetViewAtom, dropTarget);

    await Promise.all([
      this.positionMapRepository.savePositions(this.currentPositions),
      this.modelCommentDragManager.drag(context.x, context.y, context.dragStartX, context.dragStartY),
      this.descriptionPanelDragManager.drag(context.x, context.y, context.dragStartX, context.dragStartY),
      this.fileContainerDragManager.drag(context.x, context.y, context.dragStartX, context.dragStartY),
    ]);
  }

  async onDragEnd(context: DragContext): Promise<void> {
    if (!this.dragging) {
      // Example issue: https://github.com/levii/balus-app/issues/282
      console.warn(
        'NodeDragManager.onDragEnd() is called multiple times after single onDragStart(). \
                    It may cause an unpredictable problem.'
      );
    }
    if (this.dragging) {
      recordCalibrationEvent('drag.end', { durationMs: performance.now() - this.calibrationDragStartedAt });
    }
    this.dragging = false;
    getDefaultStore().set(dropTargetViewAtom, null);

    // モデル要素(付箋、ゾーン) と、コメントの位置をレイアウトにスナップさせて、保存する
    this.currentPositions = this.currentPositions.snapToLayout(ModelLayout);
    await this.positionMapRepository.savePositions(this.currentPositions);

    const targetViewData = this.isMovingFileAssetBeingDeleted() ? undefined : this.findDropTargetView(context);
    const currentViewRect = targetViewData ? this.viewRectOperations?.[this.viewId].rect : undefined;

    if (currentViewRect && targetViewData) {
      const selectedElements = this.stickyModelContentsOperation.getSelectedElementsIncludeZoneContents();

      // 選択中の要素を複製して、移動先のビューに合わせて位置を調整する
      const cloned = selectedElements
        .cloneNew(Object.fromEntries(selectedElements.assetUrls().map((url) => [url, url])))
        .move(
          -(targetViewData.rect.position.x - currentViewRect.position.x),
          -(targetViewData.rect.position.y - currentViewRect.position.y)
        );

      // ドロップ位置の矩形を保存
      const droppingRect = this.stickyModelContentsOperation.getDroppingRect();

      // NOTE: Undoしたときに元の位置に戻せるようにドラッグ開始前の位置を復元する(Undo時に戻す位置はRTDBに保管している値を参照しているため)
      this.positionMapRepository.savePositions(this.startPositions).then();
      this.modelCommentDragManager.dragEnd({ restorePositions: true }).then();
      this.descriptionPanelDragManager.dragEnd({ restorePositions: true }).then();
      this.fileContainerDragManager.dragEnd({ restorePositions: true }).then();

      const deleteCommand = await this.stickyModelContentsOperation.buildDeleteSelectedElementsCommand({
        includeZoneContents: true,
      });

      const canDropIntoZone = this.stickyModelContentsOperation.getDroppableSelectedIdsIntoZone().length > 0;

      const dropTargetZones = canDropIntoZone
        ? // 移動先のビュー内のゾーンにドロップする場合、正しくそのゾーンに属するようにdroppingRectを渡している
          this.findDropTargetZones(currentViewRect, targetViewData, droppingRect)
        : undefined;

      const command = CompositeCommand.composeOptionalCommands(
        deleteCommand,
        targetViewData.operation.buildCreateElementsCommandFromOtherModel(cloned, dropTargetZones)
      );

      if (command) {
        this.commandManager.execute(command);
        this.selectSingleView(targetViewData.view.id);
        targetViewData.operation.addToSelectionMany(cloned.elementIds());
      }
    } else {
      // 全ての要素コマンドが null, undefined の場合には、 composeOptionalCommands() は null を返す
      const command = CompositeCommand.composeOptionalCommands(
        // モデル要素(付箋、ゾーン)の位置移動
        this.buildElementsMoveCommand(),
        // モデルコメントの位置移動
        await this.modelCommentDragManager.dragEnd(),
        // ゾーンに含まれる要素の更新 (親子関係の更新)
        await this.stickyModelContentsOperation.buildSelectedElementsDropCommand(),
        this.stickyModelContentsOperation.buildUpdateParentRelationsForMovedZoneCommand(),
        // 説明パネルの位置移動
        await this.descriptionPanelDragManager.dragEnd(),
        await this.fileContainerDragManager.dragEnd()
      );

      // ドラッグの開始前・後でいずれかの要素に差分があれば、 Undo できるように CommandManager 経由で実行する
      if (command) {
        this.commandManager.execute(command);
      }
    }

    this.viewRectOperations = undefined;
  }

  private isMovingFileAssetBeingDeleted(): boolean {
    return this.stickyModelContentsOperation
      .getSelectedElementsIncludeZoneContents()
      .fileContainerAssetIds()
      .some(isFileContainerAssetBeingDeleted);
  }

  /**
   * ドロップ先のビューがある場合に、ドロップ先のゾーンがあるか探す
   */
  private findDropTargetZones(
    currentViewRect: Rect,
    targetView: { view: ViewEntity; rect: Rect; operation: StickyModelContentsOperation },
    droppingRect?: Rect | null
  ): StickyZoneId[] {
    // droppingRectが渡されていない場合は現在の状態から取得
    const rect = droppingRect ?? this.stickyModelContentsOperation.getDroppingRect();

    if (!rect) {
      return [];
    }

    // ドロップ先のビューの座標系に変換
    const translatedRect = rect.movePosition(
      currentViewRect.position.x - targetView.rect.position.x,
      currentViewRect.position.y - targetView.rect.position.y
    );

    const zones = targetView.operation.findDropTargetZonesByRect(translatedRect);

    return zones.map((zone) => zone.id);
  }

  /**
   * 付箋モデル要素(付箋、ゾーン)の位置移動コマンドを（必要な場合には）生成して返す
   *
   * @private
   */
  private buildElementsMoveCommand(): ICommand | undefined {
    // ドラッグ移動の開始前と後で位置が変化していなければ、コマンドは生成しない
    if (this.startPositions.isEqual(this.currentPositions)) {
      return;
    }

    return new ElementsMoveCommand(this.startPositions, this.currentPositions, this.positionMapRepository);
  }

  /**
   * カーソルの位置から移動先のビューがあるかを探す
   */
  private findDropTargetView(
    context: DragContext
  ): { view: ViewEntity; rect: Rect; operation: StickyModelContentsOperation } | undefined {
    const viewRectOperations = this.viewRectOperations;
    const rect = viewRectOperations?.[this.viewId]?.rect;
    if (!rect || !viewRectOperations) {
      return;
    }

    const mousePosition = {
      x: context.x + rect.position.x,
      y: context.y + rect.position.y,
    };

    if (rect.includePosition(mousePosition)) {
      return;
    }

    // マウスポインタがビューの外にある場合、別のビューに移動できないか計算してみる
    const target = Object.values(viewRectOperations).find((viewRectOperation) => {
      const { rect } = viewRectOperation;
      return (
        rect &&
        viewRectOperation.view.id !== this.viewId &&
        rect.includePosition(mousePosition) &&
        // ロックされたビューはドロップ先にならない
        !viewRectOperation.view.setting.isLocked &&
        // close 状態の意味の構造パネルの場合はドロップ先にならない
        !(viewRectOperation.view.viewType === 'meaningPanel' && !viewRectOperation.view.meaningPanelIsOpen)
      );
    });

    if (!target?.rect || !target?.operation) {
      return;
    }

    return {
      rect: target.rect,
      view: target.view,
      operation: target.operation,
    };
  }
}
